import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { api } from "../../lib/api";
import { Skeleton, EmptyState, ClearFiltersButton } from "../../components/common";
import { toast } from "sonner";

export default function UsersPage() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filters, setFilters] = useState({ q: "", brand_id: "", team_id: "" });
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, b, t] = await Promise.all([api.get("/users"), api.get("/brands"), api.get("/teams")]);
      setUsers(u.data.users); setBrands(b.data.brands); setTeams(t.data.teams);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => users.filter((u) => {
    if (filters.q && !u.email.toLowerCase().includes(filters.q.toLowerCase())) return false;
    if (filters.brand_id && u.brand?.id !== filters.brand_id) return false;
    if (filters.team_id && u.team_id !== filters.team_id) return false;
    return true;
  }), [users, filters]);

  const onAdd = async ({ email, team_id }) => {
    try { await api.post("/users", { email, team_id }); toast.success("User invited"); setShowAdd(false); load(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  const onRemove = async (id) => {
    if (!window.confirm("Remove this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">All brand users across every team.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="wl-btn-primary inline-flex items-center gap-2" data-testid="add-user-btn"><Plus size={16} /> Add User</button>
      </div>

      <div className="wl-card-solid p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          <input className="wl-input pl-9" placeholder="Search by email" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="users-search" />
        </div>
        <select className="wl-input w-auto" value={filters.brand_id} onChange={(e) => setFilters({ ...filters, brand_id: e.target.value, team_id: "" })} data-testid="users-filter-brand">
          <option value="">All brands</option>
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select className="wl-input w-auto" value={filters.team_id} onChange={(e) => setFilters({ ...filters, team_id: e.target.value })} data-testid="users-filter-team">
          <option value="">All teams</option>
          {teams.filter((t) => !filters.brand_id || t.brand_id === filters.brand_id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <ClearFiltersButton filters={filters} defaults={{ q: "", brand_id: "", team_id: "" }} onClear={setFilters} testid="users-clear-filters" />
        <div className="ml-auto text-sm text-[var(--text-secondary)]" data-testid="users-count">{filtered.length} users</div>
      </div>

      <div className="wl-card-solid overflow-hidden">
        {loading ? <div className="p-6 space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          : filtered.length === 0 ? <EmptyState title="No users" subtitle="Invite users via the team or use Add User." />
          : <table className="wl-table">
            <thead><tr><th>Email</th><th>Brand</th><th>Teams</th><th>Status</th><th>Date added</th><th>Added by</th><th className="text-right pr-6">Actions</th></tr></thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-medium">{u.email}</td>
                  <td>{u.brand?.name || "—"}</td>
                  <td><div className="flex flex-wrap gap-1">{(u.all_teams || [u.team]).filter(Boolean).map((t) => <span key={t.id} className="wl-pill wl-pill-neutral">{t.name}</span>)}</div></td>
                  <td><span className={`wl-pill ${u.status === "active" ? "wl-pill-active" : "wl-pill-pending"}`}>{u.status}</span></td>
                  <td className="text-[var(--text-secondary)]">{new Date(u.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="text-[var(--text-secondary)] text-xs">{u.invited_by || "—"}</td>
                  <td className="text-right pr-6"><button onClick={() => onRemove(u.id)} className="wl-btn-destructive" data-testid={`user-remove-${u.id}`}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>}
      </div>

      {showAdd && <AddUserModal brands={brands} teams={teams} onClose={() => setShowAdd(false)} onAdd={onAdd} />}
    </div>
  );
}

function AddUserModal({ brands, teams, onClose, onAdd }) {
  const [brandId, setBrandId] = useState(brands[0]?.id || "");
  const [teamId, setTeamId] = useState("");
  const [email, setEmail] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center wl-fade-in" style={{ background: "rgba(26,31,54,0.4)", backdropFilter: "blur(4px)" }}>
      <div className="wl-card-solid bg-white w-full max-w-md p-6 wl-scale-in shadow-xl">
        <h2 className="font-display text-lg font-semibold mb-4">Add user</h2>
        <form onSubmit={(e) => { e.preventDefault(); onAdd({ email, team_id: teamId }); }}>
          <label className="wl-label">Brand</label>
          <select className="wl-input mt-1 mb-3" value={brandId} onChange={(e) => { setBrandId(e.target.value); setTeamId(""); }} required>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <label className="wl-label">Team</label>
          <select className="wl-input mt-1 mb-3" value={teamId} onChange={(e) => setTeamId(e.target.value)} required data-testid="add-user-team">
            <option value="">Select team…</option>
            {teams.filter((t) => t.brand_id === brandId).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <label className="wl-label">Email</label>
          <input className="wl-input mt-1 mb-4" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="add-user-email" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="wl-btn-ghost">Cancel</button>
            <button className="wl-btn-primary" data-testid="add-user-submit">Send invite</button>
          </div>
        </form>
      </div>
    </div>
  );
}
